import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ModernCalendar } from '@/components/ui/modern-calendar'
import { Textarea } from '@/components/ui/textarea'
import { useBookingQueries } from '@/hooks/queries/useBookingQueries'
import { usePropertyQueries } from '@/hooks/queries/usePropertyQueries'
import { generateOpenTimeSlots } from '@/lib/bookingUtils'
import { cn, formatFullAddress, formatPrice } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { addDays, format, isBefore, startOfDay } from 'date-fns'
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Check,
  ChevronRight,
  Home
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

export const Route = createFileRoute('/_public/bookings/create/$propertyId')({
  component: BookShowingPage,
})

const bookingFormSchema = z.object({
  start_time: z.date(),
  end_time: z.date(),
  visitor_name: z.string().min(1, "Name is required"),
  visitor_email: z.string().email("Invalid email address").min(1, "Email is required"),
  visitor_phone: z.string().min(1, "Phone number is required"),
  notes: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingFormSchema>;

function BookShowingPage() {
  const [step, setStep] = useState<'date' | 'time' | 'details' | 'confirmation'>('date')
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [selectedTime, setSelectedTime] = useState<Date | undefined>()

  const today = useMemo(() => new Date(), [])

  const { propertyId } = useParams({ from: "/_public/bookings/create/$propertyId" })
  const { useFetchProperty } = usePropertyQueries();
  const { data: property } = useFetchProperty(propertyId);

  const { useFetchBookings, useFetchAvailabilities, createBookingMutation } = useBookingQueries();

  const { data: availabilities } = useFetchAvailabilities(propertyId);
  const { data: bookingsData } = useFetchBookings(propertyId, "active");

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      start_time: undefined,
      end_time: undefined,
      visitor_name: '',
      visitor_email: '',
      visitor_phone: '',
    },
  });


  const availableTimeSlots = useMemo(() => {
    return generateOpenTimeSlots(bookingsData || [], availabilities || [], new Date(), addDays(new Date(), 30));
  }, [availabilities, bookingsData])

  const handleDateSelect = (date: Date | undefined) => {
    if (date && !isBefore(date, today)) {
      setSelectedDate(date)
      setStep('time')
    }
  }

  const handleTimeSelect = (start_time: Date, end_time: Date) => {
    setSelectedTime(start_time)
    form.setValue('start_time', start_time)
    form.setValue('end_time', end_time)
    setStep('details')
  }

  const onSubmit: SubmitHandler<BookingFormValues> = async (data) => {
    await createBookingMutation.mutateAsync({
      property_id: propertyId,
      start_datetime: data.start_time.toISOString(),
      end_datetime: data.end_time.toISOString(),
      visitor_name: data.visitor_name,
      visitor_email: data.visitor_email,
      visitor_phone: data.visitor_phone,
      notes: data.notes,
    }, {
      onSuccess: () => {
        setStep('confirmation')
      }
    })

    console.log('Form Data:', data);
  }

  const resetBooking = () => {
    setStep('date')
    setSelectedDate(undefined)
    setSelectedTime(undefined)
    form.reset({
      visitor_name: '',
      visitor_email: '',
      visitor_phone: '',
      notes: '',
    });
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 to-blue-50">
        <p className="text-slate-700 text-lg">Loading property details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 border-l border-slate-300" />
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Home className="h-4 w-4" />
                Book a Showing
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${step === 'date' ? 'bg-blue-500' : step === 'time' || step === 'details' || step === 'confirmation' ? 'bg-green-500' : 'bg-slate-300'}`} />
              <div className={`w-2 h-2 rounded-full ${step === 'time' ? 'bg-blue-500' : step === 'details' || step === 'confirmation' ? 'bg-green-500' : 'bg-slate-300'}`} />
              <div className={`w-2 h-2 rounded-full ${step === 'details' ? 'bg-blue-500' : step === 'confirmation' ? 'bg-green-500' : 'bg-slate-300'}`} />
              <div className={`w-2 h-2 rounded-full ${step === 'confirmation' ? 'bg-green-500' : 'bg-slate-300'}`} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Booking Flow */}
          <div>
            <div className="space-y-8">
              {step !== 'confirmation' && (
                <div className="text-center space-y-3">
                  <h1 className="text-4xl font-semibold text-slate-900">
                    {step === 'date' && 'Select a date'}
                    {step === 'time' && 'Select a time'}
                    {step === 'details' && 'Your details'}
                  </h1>
                  <p className="text-lg text-slate-600">
                    {step === 'date' && 'Choose your preferred date for the property showing'}
                    {step === 'time' && `Available times for ${selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : ''}`}
                    {step === 'details' && 'Complete your booking details'}
                  </p>
                </div>
              )}

              {step !== 'confirmation' && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">

                    {/* Static Property & Booking Summary Sidebar */}
                    <div className="lg:col-span-4 bg-linear-to-br from-slate-50 to-blue-50 p-8 flex flex-col">
                      <div className="space-y-6">
                        {/* Property Image */}
                        <div className="aspect-4/3 relative overflow-hidden rounded-xl shadow-lg">
                          <img
                            src={property?.images?.[0].original_url || 'https://via.placeholder.com/400x300?text=No+Image'}
                            alt={property ? formatFullAddress(property) : 'Property image'}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent" />
                          <Badge className="absolute top-3 left-3 bg-white/95 text-slate-800 border-0 shadow-sm">
                            {property ? (property.is_freehold ? property.freehold_property_type : property.condo_property_type) : 'N/A'}
                          </Badge>
                          <div className="absolute bottom-3 left-3 text-white">
                            <p className="text-2xl font-bold drop-shadow-lg">{property ? formatPrice(property.list_price) : 'Price N/A'}</p>
                          </div>
                          {step !== 'date' && (
                            <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-lg text-xs font-medium shadow-lg">
                              ✓ {step === 'time' ? 'Date Selected' : step === 'details' ? 'Time Selected' : 'Booking Progress'}
                            </div>
                          )}
                        </div>

                        {/* Property Details */}
                        <div className="space-y-4">
                          <h3 className="text-xl font-semibold text-slate-900 mb-2">
                            {property ? formatFullAddress(property) : 'Address N/A'}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-slate-600 bg-white/60 rounded-lg px-3 py-2">
                            <span className="flex items-center gap-1">
                              <Home className="h-4 w-4" />
                              {property?.total_bedrooms || 'N/A'} beds
                            </span>
                            <span>{property?.total_bathrooms || 'N/A'} baths</span>
                            <span>{property?.approx_square_footage || 'N/A'} sqft</span>
                          </div>
                        </div>
                      </div>

                      {/* Booking Status - Updates Based on Progress */}
                      <div className="mt-auto pt-6">
                        {/* Navigation */}
                        {step !== 'date' && (
                          <div className="mt-4">
                            <Button
                              variant="ghost"
                              onClick={() => step === 'time' ? setStep('date') : setStep('time')}
                              className="w-full text-slate-600 hover:text-slate-900 hover:bg-white/50 border border-white/50"
                            >
                              <ArrowLeft className="h-4 w-4 mr-2" />
                              {step === 'time' ? 'Change Date' : 'Change Time'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dynamic Content Area */}
                    <div className="lg:col-span-8 p-8 flex flex-col">
                      {step === 'date' && (
                        <div className="h-full flex flex-col">
                          <div className="flex-1">
                            <ModernCalendar
                              selectedDate={selectedDate}
                              onDateSelect={handleDateSelect}
                              disabled={(date) => {
                                return isBefore(date, new Date()) || !availableTimeSlots[startOfDay(date).toISOString()]
                              }}
                            />
                          </div>

                          {/* Information Section */}
                          <div className="mt-6 space-y-6">
                            <div className="border-t border-slate-200 pt-6">
                              <div className="text-center text-slate-500">
                                <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Select a date above to continue</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 'time' && (
                        <div className="flex flex-col h-full">
                          <div className="mb-8">
                            <h3 className="text-2xl font-semibold text-slate-900 mb-2">Choose your time</h3>
                            <p className="text-slate-600">Select the perfect time for your property showing</p>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 flex-1 content-start">
                            {selectedDate && availableTimeSlots[selectedDate.toISOString()] && availableTimeSlots[selectedDate.toISOString()].map((slot) => (
                              <button
                                key={slot.start.toISOString()}
                                className={cn(
                                  "p-4 rounded-xl text-center transition-all duration-200 border-2 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[80px] flex flex-col justify-center",
                                  {
                                    'bg-blue-600 text-white border-blue-600 shadow-xl shadow-blue-200': selectedTime === slot.start,
                                    'bg-white text-slate-900 border-slate-200 hover:border-blue-300 hover:bg-blue-50 shadow-sm hover:shadow-md': selectedTime !== slot.start
                                  }
                                )}
                                onClick={() => { handleTimeSelect(slot.start, slot.end) }}
                              >
                                <div className="text-lg font-semibold">{format(slot.start, 'hh:mm a')}</div>
                                <div className="text-sm opacity-75 mt-1">
                                  {selectedTime === slot.start ? 'Selected' : 'Available'}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {step === 'details' && (
                        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col h-full">
                          <div className="mb-8">
                            <h3 className="text-2xl font-semibold text-slate-900 mb-2">Your Details</h3>
                            <p className="text-slate-600">
                              {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')} at {selectedTime && format(selectedTime, 'h:mm a')}
                            </p>
                          </div>

                          <div className="space-y-6 flex-1">
                            {/* Contact Form */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="name">Full Name *</Label>
                                <Input
                                  id="name"
                                  {...form.register("visitor_name")}
                                  placeholder="Enter your full name"
                                />
                                {form.formState.errors.visitor_name && (
                                  <p className="text-sm text-red-500">{form.formState.errors.visitor_name.message}</p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                  id="email"
                                  type="email"
                                  {...form.register("visitor_email")}
                                  placeholder="Enter your email"
                                />
                                {form.formState.errors.visitor_email && (
                                  <p className="text-sm text-red-500">{form.formState.errors.visitor_email.message}</p>
                                )}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="phone">Phone Number *</Label>
                              <Input
                                id="phone"
                                {...form.register("visitor_phone")}
                                placeholder="Enter your phone number"
                              />
                              {form.formState.errors.visitor_phone && (
                                <p className="text-sm text-red-500">{form.formState.errors.visitor_phone.message}</p>
                              )}
                            </div>


                            <div className="space-y-2">
                              <Label htmlFor="message">Additional Notes (Optional)</Label>
                              <Textarea
                                id="message"
                                {...form.register("notes")}
                                rows={3}
                              />
                            </div>
                          </div>

                          <div className="mt-8 pt-6 border-t border-slate-200">
                            <Button
                              type="submit"
                              disabled={form.formState.isSubmitting}
                              className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                              {form.formState.isSubmitting ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                  Confirming Booking...
                                </>
                              ) : (
                                <>
                                  Confirm Booking
                                  <ChevronRight className="h-4 w-4 ml-2" />
                                </>
                              )}
                            </Button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 'confirmation' && (
                <div className="max-w-2xl mx-auto text-center">
                  {/* Success Icon & Message */}
                  <div className="mb-8">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-semibold text-slate-900 mb-2">Booking confirmed</h1>
                    <p className="text-lg text-slate-600">
                      Your property showing is scheduled for {selectedDate && format(selectedDate, 'EEEE, MMMM d')} at {selectedTime && format(selectedTime, 'h:mm a')}
                    </p>
                  </div>

                  {/* Simple Details Card */}
                  <div className="bg-white border border-slate-200 rounded-lg p-8 mb-8 text-left">
                    <div className="space-y-6">
                      {/* Property */}
                      <div>
                        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Property</h3>
                        <div className="flex items-center gap-4">
                          <img
                            src={property?.images?.[0].original_url || 'https://via.placeholder.com/100x100?text=No+Image'}
                            alt={property ? formatFullAddress(property) : 'Property image'}
                            className="w-12 h-12 rounded object-cover"
                          />
                          <div>
                            <p className="font-medium text-slate-900">{property ? formatFullAddress(property) : 'Address N/A'}</p>
                            <p className="text-sm text-slate-600">{property ? formatPrice(property.list_price) : 'Price N/A'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-slate-200"></div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-slate-500 mb-1">Date & Time</p>
                          <p className="text-slate-900">{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</p>
                          <p className="text-slate-900">{selectedTime && format(selectedTime, 'hh:mm a')}</p>
                        </div>
                        {/* Contact Info */}
                        <div>
                          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Contact Details</h3>
                          <div className="space-y-1">
                            <p className="text-slate-900">{form.getValues("visitor_name")}</p>
                            <p className="text-slate-600">{form.getValues("visitor_email")}</p>
                            <p className="text-slate-600">{form.getValues("visitor_phone")}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Simple Next Steps */}
                  <div className="bg-slate-50 rounded-lg p-6 mb-8">
                    <h3 className="font-medium text-slate-900 mb-4">What's next?</h3>
                    <div className="text-sm text-slate-600 space-y-2">
                      <p>• Confirmation email sent to {form.getValues("visitor_email")}</p>
                      <p>• You'll receive a calendar invite with meeting details</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      variant="outline"
                      onClick={resetBooking}
                      className="px-6 py-2"
                    >
                      Book another showing
                    </Button>
                    <Link
                      to="/properties/$id"
                      params={{ id: propertyId }}>
                      <Button
                        className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        Return to property
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {step !== 'confirmation' && (
                <div className="text-center">
                  <p className="text-sm text-slate-500">
                    📍 All times shown in Eastern Time Zone
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
